"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SchedulerInstantMeetingCalendar = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
require("./schedulerInstantMeetingCalendar.css");
const components_1 = require("@/app/shared/components");
const constants_1 = require("@/app/shared/constants");
const utils_1 = require("@/app/shared/helpers/utils");
const labels_1 = __importDefault(require("@/app/shared/i18n/labels"));
const ReplacesEnum_1 = require("@/app/shared/i18n/ReplacesEnum");
const notifications_1 = require("@/app/shared/store/notifications");
const components_2 = require("@/app/SUMO/components");
const scheduler_1 = require("@/app/SUMO/store/scheduler");
const interaction_1 = __importDefault(require("@fullcalendar/interaction"));
const moment_timezone_1 = __importDefault(require("@fullcalendar/moment-timezone"));
const react_1 = __importDefault(require("@fullcalendar/react"));
const timegrid_1 = __importDefault(require("@fullcalendar/timegrid"));
const outline_1 = require("@heroicons/react/24/outline");
const moment_timezone_2 = __importDefault(require("moment-timezone"));
const button_1 = require("primereact/button");
const dropdown_1 = require("primereact/dropdown");
const inputswitch_1 = require("primereact/inputswitch");
const messages_1 = require("primereact/messages");
const progressspinner_1 = require("primereact/progressspinner");
const skeleton_1 = require("primereact/skeleton");
const utils_2 = require("primereact/utils");
const react_2 = require("react");
const react_redux_1 = require("react-redux");
const lib_1 = require("use-debounce/lib");
const uuid_1 = require("uuid");
const SchedulerInstantMeetingCalendar = () => {
    const calendarRef = (0, react_2.useRef)(null);
    const msgs = (0, react_2.useRef)(null);
    const dispatch = (0, react_redux_1.useDispatch)();
    const isFetching = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectIsHostScheduleFetching);
    const isError = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectIsHostScheduleError);
    const showWeekends = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectShowWeekends);
    const availabilityCustom = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectAvailabilityCustom);
    const bufferCustom = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectBufferCustom);
    const hostScheduleDisabled = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectFilteredHostScheduleDisabled);
    const hostScheduleAppointments = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectFilteredHostScheduleAppointments);
    const isCustomAvailability = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectIsCustomAvailability);
    const isLoaded = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectIsSchedulerLoaded);
    const timezone = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectCalendarTimezone);
    const customName = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectCustomName);
    const bufferBefore = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectBufferBefore);
    const bufferAfter = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectBufferAfter);
    const bufferBeforeInMs = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectBufferBeforeInMs);
    const bufferAfterInMs = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectBufferAfterInMs);
    const duration = (0, react_redux_1.useSelector)(scheduler_1.schedulerSelectors.selectDuration);
    const [allowBack, setAllowBack] = (0, react_2.useState)(false);
    const [allowNext, setAllowNext] = (0, react_2.useState)(false);
    const [title, setTitle] = (0, react_2.useState)('');
    // TODO: move timezones to selectors (selectTimezones, selectIsTimezoneValid) and add timezone validation like in SUMOCEP
    const timezones = moment_timezone_2.default.tz.names().map((tz) => {
        const offset = moment_timezone_2.default.tz(tz).format('Z');
        const zone = `GMT${offset.slice(0, 3)}`;
        return {
            label: `${tz} (${zone})`,
            zone,
            value: tz,
        };
    });
    const [handleUpdateEvents] = (0, lib_1.useDebouncedCallback)(() => {
        if (isLoaded) {
            updateTitle(false);
            const needsTitleUpdate = availabilityCustom.some((event) => event.title !== customName);
            if (needsTitleUpdate) {
                dispatch(scheduler_1.SUMO1SchedulerActions.setAvailabilityCustom(availabilityCustom.map((event) => ({ ...event, title: customName }))));
            }
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.setOption('snapDuration', getSnapDuration());
                calendarApi.setOption('defaultTimedEventDuration', {
                    milliseconds: (0, utils_1.convertTimeToMsWithTimeUnit)(duration?.count, duration?.timeUnit),
                });
                calendarApi.refetchEvents();
            }
        }
    }, 500);
    (0, react_2.useEffect)(() => {
        handleUpdateEvents();
    }, [customName, bufferBeforeInMs, bufferAfterInMs, duration, isLoaded]);
    (0, react_2.useEffect)(() => {
        if (isLoaded) {
            updateTitle();
        }
    }, [isLoaded]);
    (0, react_2.useEffect)(() => {
        handleCalendarError();
    }, [isError]);
    (0, react_2.useEffect)(() => {
        if (!isCustomAvailability) {
            dispatch(scheduler_1.SUMO1SchedulerActions.setAvailabilityCustom([]));
        }
    }, [isCustomAvailability]);
    const handleCalendarError = () => {
        if (msgs.current) {
            msgs.current.clear();
            if (isError) {
                msgs.current.show([{ sticky: true, severity: 'info', detail: labels_1.default.INTERNAL_SERVER_ERROR_ALERT }]);
            }
        }
    };
    const handleOnTryAgain = () => {
        dispatch(scheduler_1.SUMO1SchedulerActions.getHostScheduleRequest());
    };
    const handleEventsRequest = (requestParameters, onSuccess) => {
        if (isFetching || !isLoaded) {
            onSuccess([]);
        }
        const isTodayVisible = new Date(requestParameters.startStr).getTime() <= Date.now() && Date.now() <= new Date(requestParameters.endStr).getTime();
        setAllowBack(!isTodayVisible);
        setAllowNext(new Date(requestParameters.endStr) < scheduler_1.ONE_YEAR_LIMIT_DATE);
        onSuccess([...hostScheduleDisabled, ...hostScheduleAppointments, ...availabilityCustom, ...bufferCustom]);
    };
    const renderEventContent = ({ event }) => {
        const { extendedProps, id, title } = event;
        const handleRemoveClick = () => {
            dispatch(scheduler_1.SUMO1SchedulerActions.setAvailabilityCustom(availabilityCustom.filter((e) => e.id !== id)));
        };
        if (extendedProps.type === 'BUFFER_BEFORE' || extendedProps.type === 'BUFFER_AFTER') {
            return (0, jsx_runtime_1.jsx)("div", { className: 'scheduler-event-content', children: title });
        }
        return ((0, jsx_runtime_1.jsxs)("div", { className: 'scheduler-event-content', children: [(0, jsx_runtime_1.jsx)("div", { className: 'fc-event-title-truncate', title: title, children: title }), extendedProps.type === 'SELECTED_RANGE' && ((0, jsx_runtime_1.jsx)(outline_1.XMarkIcon, { className: 'icon-10px absolute right-0 top-0 cursor-pointer text-primary-white', onClick: handleRemoveClick }))] }));
    };
    const getApproveStatus = (selectInfo, { includeBufferCustom = true } = { includeBufferCustom: true }) => {
        const selectStartWithBuffer = new Date(selectInfo.start.getTime() - bufferBeforeInMs);
        const selectEndWithBuffer = new Date(selectInfo.start.getTime() + (0, utils_1.convertTimeToMsWithTimeUnit)(duration?.count, duration?.timeUnit) + bufferAfterInMs);
        const isBeforeNow = selectStartWithBuffer < new Date();
        if (isBeforeNow)
            return false;
        const isAllowed = selectEndWithBuffer < scheduler_1.ONE_YEAR_LIMIT_DATE;
        const blockers = includeBufferCustom
            ? [...hostScheduleDisabled, ...hostScheduleAppointments, ...bufferCustom]
            : [...hostScheduleDisabled, ...hostScheduleAppointments];
        const isDisabled = blockers.some((disabled) => {
            const start = new Date(disabled.start);
            const end = new Date(disabled.end);
            const hasOverlap = (selectStartWithBuffer >= start && selectStartWithBuffer < end) ||
                (selectEndWithBuffer > start && selectEndWithBuffer <= end) ||
                (selectStartWithBuffer <= start && selectEndWithBuffer >= end);
            return hasOverlap;
        });
        return isAllowed && !isDisabled;
    };
    const showSelectError = () => {
        const durationTimeUnitLabel = duration?.timeUnit === constants_1.TimeUnit.MINUTE
            ? labels_1.default.SCHEDULER_DURATION_DIMENSION_MINUTES_SHORT_LABEL
            : labels_1.default.SCHEDULER_DURATION_DIMENSION_HOURS_SHORT_LABEL;
        const bufferAfterMinutes = utils_1.bufferTimeConvert.fromEnumToNumber(bufferAfter);
        const bufferBeforeMinutes = utils_1.bufferTimeConvert.fromEnumToNumber(bufferBefore);
        const totalBufferTimeMinutes = bufferBeforeMinutes + bufferAfterMinutes;
        const durationString = `${duration?.count?.toString()} ${durationTimeUnitLabel} ${labels_1.default.SCHEDULER_DURATION}`;
        const bufferString = totalBufferTimeMinutes
            ? ` + ${totalBufferTimeMinutes} ${labels_1.default.SCHEDULER_DURATION_DIMENSION_MINUTES_SHORT_LABEL} ${labels_1.default.SCHEDULER_BUFFER}`
            : '';
        dispatch(notifications_1.notificationsActions.showToast({
            id: constants_1.TOAST_ERROR_ID.SCHEDULER_TIME_SLOT_TO_SHORT,
            data: { replaceDetailKeys: { [ReplacesEnum_1.ReplacesDetailEnum.DURATION]: durationString + bufferString } },
        }));
    };
    const selectAllow = (selectInfo) => {
        const isApproved = getApproveStatus(selectInfo);
        if (!isApproved) {
            showSelectError();
        }
        return isApproved;
    };
    (0, react_2.useEffect)(() => {
        const [custom] = availabilityCustom;
        if (!custom)
            return;
        const isApproved = getApproveStatus({
            start: new Date(custom.start),
            end: new Date(custom.end),
            startStr: custom.start,
            endStr: custom.end,
            allDay: false,
        }, { includeBufferCustom: false });
        if (!isApproved) {
            dispatch(scheduler_1.SUMO1SchedulerActions.setAvailabilityCustom([]));
        }
    }, [hostScheduleDisabled]);
    const handleSelect = (event) => {
        const selectedDuration = event.end.getTime() - event.start.getTime();
        const durationInMs = (0, utils_1.convertTimeToMsWithTimeUnit)(duration?.count, duration?.timeUnit);
        const isDurationValid = selectedDuration >= durationInMs;
        const newEndDate = new Date(event.start.getTime() + durationInMs);
        const newSelectedDuration = newEndDate.getTime() - event.start.getTime();
        const newDurationCount = (0, utils_1.convertMsToTimeUnit)(isDurationValid ? selectedDuration : newSelectedDuration, constants_1.TimeUnit.MINUTE);
        dispatch(scheduler_1.SUMO1SchedulerActions.setAvailabilityCustom([
            {
                id: (0, uuid_1.v4)(),
                title: customName,
                type: 'SELECTED_RANGE',
                start: event.startStr,
                end: isDurationValid ? event.endStr : newEndDate.toISOString(),
                borderColor: 'var(--blue-100)',
                backgroundColor: 'var(--blue-100)',
                textColor: 'var(--primary-white)',
            },
        ]));
        dispatch(scheduler_1.SUMO1SchedulerActions.updateBookingPageWhen({ duration: { count: newDurationCount, timeUnit: constants_1.TimeUnit.MINUTE } }));
    };
    const updateTitle = (refetch = true) => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi)
            return;
        setTitle(calendarApi.view.title);
        const date = calendarApi.getDate().getTime();
        dispatch(scheduler_1.SUMO1SchedulerActions.setCalendarStartAt(date));
        if (refetch) {
            dispatch(scheduler_1.SUMO1SchedulerActions.getHostScheduleRequest());
        }
    };
    const handlePrev = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi)
            return;
        calendarApi.prev();
        updateTitle();
    };
    const handleNext = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi)
            return;
        let nextDate = new Date();
        nextDate.setDate(calendarApi.getDate().getDate() + 7);
        if (nextDate < scheduler_1.ONE_YEAR_LIMIT_DATE) {
            calendarApi.next();
            updateTitle();
        }
    };
    const handleToday = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi)
            return;
        calendarApi.gotoDate(scheduler_1.DEFAULT_CALENDAR_START_AT);
        updateTitle();
    };
    const handleChangeTimezone = (event) => {
        dispatch(scheduler_1.SUMO1SchedulerActions.setTimeZone(event.value));
    };
    const getScrollTime = () => {
        const now = new Date();
        now.setHours(now.getHours() - 3); // adjust to 3 hours before now
        return now.toTimeString().slice(0, 8); // format: "HH:MM:SS"
    };
    const onCalendarSettingsChange = (value) => {
        dispatch(scheduler_1.SUMO1SchedulerActions.setShowWeekends(value));
        components_2.Settings.setCalendarShowWeekends(value);
    };
    const getDayHeaderContent = (args) => {
        const date = args.date;
        const day = date.toLocaleDateString('en-US', { weekday: 'short' });
        const dayNumber = date.getDate();
        const isToday = date.toDateString() === new Date().toDateString();
        return ((0, jsx_runtime_1.jsxs)("div", { className: 'flex-center flex-column gap-2px', children: [(0, jsx_runtime_1.jsx)("div", { className: isToday ? 'text-gray-100' : 'text-gray-60', style: { fontSize: '9px', lineHeight: '11px' }, children: day }), (0, jsx_runtime_1.jsx)("div", { className: (0, utils_2.classNames)('text-body-s-reg text-gray-60', isToday && 'bg-gray-100 text-primary-white px-6px border-radius-2px'), children: dayNumber })] }));
    };
    const getSnapDuration = () => {
        if (!duration?.count)
            return scheduler_1.DEFAULT_SNAP_DURATION;
        const count = duration.count.toString().padStart(2, '0');
        switch (duration.timeUnit) {
            case constants_1.TimeUnit.MINUTE:
                return `00:${count}:00`;
            case constants_1.TimeUnit.HOUR:
                return `${count}:00:00`;
            default:
                return scheduler_1.DEFAULT_SNAP_DURATION;
        }
    };
    const getHighlightMinHeight = () => {
        if (!duration?.count || !duration.timeUnit)
            return `${constants_1.FULL_CALENDAR_CELL_HEIGHT}px`;
        const durationInMs = (0, utils_1.convertTimeToMsWithTimeUnit)(duration.count, duration.timeUnit);
        const durationInMinutes = durationInMs / (60 * 1000);
        const pixelsPerMinute = constants_1.FULL_CALENDAR_CELL_HEIGHT / scheduler_1.DEFAULT_WHEN.duration.count;
        return `${durationInMinutes * pixelsPerMinute}px`;
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: 'flex flex-column h-full', children: [(0, jsx_runtime_1.jsxs)("div", { className: 'flex-none flex-left-center gap-8px p-8px', children: [(0, jsx_runtime_1.jsxs)("div", { className: 'flex-left-center gap-12px flex-1', children: [!isLoaded ? (0, jsx_runtime_1.jsx)(skeleton_1.Skeleton, { className: 'w-120px h-32px border-radius-6px' }) : (0, jsx_runtime_1.jsx)("div", { className: 'text-title-s-med', children: title }), isFetching && ((0, jsx_runtime_1.jsx)(progressspinner_1.ProgressSpinner, { className: 'w-18px h-18px m-0', strokeWidth: '5' }))] }), (0, jsx_runtime_1.jsxs)("div", { className: 'flex-left-center gap-10px mr-32px', children: [(0, jsx_runtime_1.jsx)(inputswitch_1.InputSwitch, { checked: showWeekends, onChange: () => onCalendarSettingsChange(!showWeekends) }), (0, jsx_runtime_1.jsx)("div", { className: 'text-body-s-reg', children: labels_1.default.SHOW_WEEKENDS })] }), (0, jsx_runtime_1.jsx)(button_1.Button, { title: labels_1.default.SCHEDULER_BUTTON_PREVIOUS_WEEK, className: 'flex-center w-32px h-32px p-0', icon: (0, jsx_runtime_1.jsx)(outline_1.ChevronLeftIcon, { className: 'icon-18px' }), outlined: true, onClick: handlePrev, disabled: !allowBack }), (0, jsx_runtime_1.jsx)(button_1.Button, { title: labels_1.default.SCHEDULER_BUTTON_THIS_WEEK, className: 'button-sm', outlined: true, label: labels_1.default.SCHEDULER_BUTTON_TODAY, onClick: handleToday, disabled: !allowBack }), (0, jsx_runtime_1.jsx)(button_1.Button, { title: labels_1.default.SCHEDULER_BUTTON_NEXT_WEEK, className: 'flex-center w-32px h-32px p-0', icon: (0, jsx_runtime_1.jsx)(outline_1.ChevronRightIcon, { className: 'icon-18px' }), outlined: true, onClick: handleNext, disabled: !allowNext })] }), (0, jsx_runtime_1.jsx)("div", { className: 'flex-1 relative', style: { '--fc-highlight-min-height': getHighlightMinHeight() }, children: isError ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(messages_1.Messages, { ref: msgs, className: 'absolute top-0 left-0 right-0' }), !isFetching && ((0, jsx_runtime_1.jsx)(components_1.GlobalError, { onTryAgain: handleOnTryAgain, errorTitle: labels_1.default.INTERNAL_SERVER_ERROR, errorMessage: labels_1.default.PLEASE_WAIT_AND_TRY_AGAIN, tryAgainLabel: labels_1.default.TRY_AGAIN }))] })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(dropdown_1.Dropdown, { value: timezone, options: timezones, onChange: handleChangeTimezone, optionLabel: 'label', filter: true, className: 'absolute top-1px left-0 z-5 w-40px h-40px flex-center border-none bg-transparent', placeholder: 'Select Timezone', pt: {
                                trigger: { className: 'p-dropdown-trigger hidden' },
                                input: { className: 'text-table-timezone text-center bg-transparent p-4px' },
                            }, valueTemplate: (option) => `${option.zone}` }), (0, jsx_runtime_1.jsx)(react_1.default, { ref: calendarRef, plugins: [timegrid_1.default, interaction_1.default, moment_timezone_1.default], initialDate: scheduler_1.DEFAULT_CALENDAR_START_AT, initialView: 'timeGridWeek', weekends: showWeekends, selectable: isCustomAvailability, selectAllow: selectAllow, select: handleSelect, selectOverlap: true, events: handleEventsRequest, eventContent: renderEventContent, eventOverlap: true, height: '100%', allDaySlot: false, headerToolbar: false, scrollTime: getScrollTime(), nowIndicator: true, dayHeaderContent: getDayHeaderContent, timeZone: timezone, slotDuration: scheduler_1.DEFAULT_SNAP_DURATION }, timezone)] })) })] }));
};
exports.SchedulerInstantMeetingCalendar = SchedulerInstantMeetingCalendar;
